(() => {
  if (window.AsenmoShopping) return;
  const cartUrl = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  async function request(url, body, sectionRender = false) {
    const response = await fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : sectionRender ? {} : { headers: { Accept: 'application/json' } });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || 'Your cart could not be updated.');
      error.rejected = response.status >= 400 && response.status < 500 || Number(data.status) >= 400 && Number(data.status) < 500;
      throw error;
    }
    return data;
  }
  const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const api = {
    busy: false,
    request,
    async replaceLine(key, variant, expectedQuantity) {
      const before = await request(`${cartUrl()}.js`);
      const original = before.items.find((item) => item.key === key);
      if (!original || original.quantity !== expectedQuantity) throw new Error('Your cart has changed. Refresh it before changing this item.');
      if (String(original.variant_id) === String(variant)) return before;
      if (original.selling_plan_allocation || original.item_components?.length || original.parent_relationship || original.instructions?.can_remove === false || original.instructions?.can_update_quantity === false) throw new Error('Open this product to change its purchase options.');
      // Add first so a rejected replacement never removes the original item.
      // Shopify has no atomic variant-replacement endpoint. Never retry a mutation automatically.
      await request(window.routes?.cart_add_url || `${cartUrl()}/add.js`, { items: [{ id: Number(variant), quantity: original.quantity, properties: original.properties || {} }] });
      const after = await request(`${cartUrl()}.js`);
      // Discounts can change a line key after an add, so re-identify the original line.
      const matches = after.items.filter((item) => item.variant_id === original.variant_id && item.quantity === original.quantity && !item.selling_plan_allocation && propertiesKey(item.properties) === propertiesKey(original.properties));
      if (matches.length !== 1) throw new Error('Your cart changed during the update. Review it before making another change.');
      return request(window.routes?.cart_change_url || `${cartUrl()}/change.js`, { id: matches[0].key, quantity: 0 });
    },
    async refresh() {
      const sectionNames = ['cart-drawer', 'cart-icon-bubble'];
      const main = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (main) sectionNames.push(main.dataset.id || 'main-cart-items');
      if (footer) sectionNames.push(footer.dataset.id || 'main-cart-footer');
      const sections = await request(`${cartUrl()}?sections=${sectionNames.join(',')}`, undefined, true);
      const replace = (html, selector, target) => {
        const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        if (!source || !target) throw new Error('Refresh the cart to see its latest contents.');
        target.innerHTML = source.innerHTML;
      };
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        replace(sections['cart-drawer'], '#CartDrawer', document.getElementById('CartDrawer'));
        drawer.classList.toggle('is-empty', Boolean(drawer.querySelector('.drawer__inner-empty')));
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
        if (drawer.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'));
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) replace(sections['cart-icon-bubble'], '.shopify-section', bubble);
      if (main) replace(sections[main.dataset.id || 'main-cart-items'], '.js-contents', main.querySelector('.js-contents'));
      if (footer) {
        const selector = footer.querySelector('.cart__footer') ? '.cart__footer' : '.js-contents';
        if (footer.querySelector(selector)) replace(sections[footer.dataset.id || 'main-cart-footer'], selector, footer.querySelector(selector));
      }
      if (main) {
        const empty = !main.querySelector('.cart-item');
        main.closest('cart-items')?.classList.toggle('is-empty', empty);
        footer?.classList.toggle('is-empty', empty);
        const checkout = footer?.querySelector('[name="checkout"]');
        if (checkout) checkout.disabled = empty;
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    },
  };
  window.AsenmoShopping = api;
  // Avoid competing user actions while this theme's multi-step change is running.
  ['click', 'change', 'submit'].forEach((type) => document.addEventListener(type, (event) => {
    if (!api.busy || !event.target.closest?.('cart-items, cart-drawer, product-form, complete-the-set')) return;
    if (type === 'change' && event.target.matches?.('.quantity__input')) {
      event.target.value = event.target.getAttribute('value');
    }
    event.preventDefault(); event.stopImmediatePropagation();
  }, true));

  class CartVariantEditor extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.button = this.querySelector('[data-cart-option-submit]');
      this.onClick ||= () => this.update();
      this.button.addEventListener('click', this.onClick);
    }
    disconnectedCallback() { this.button?.removeEventListener('click', this.onClick); }
    async update() {
      if (api.busy || this.needsReview) return;
      const select = this.querySelector('select');
      if (select.value === this.dataset.variant) return;
      api.busy = true; this.button.disabled = true;
      const status = this.querySelector('[data-shopping-status]');
      status.textContent = 'Updating item…';
      try {
        await api.replaceLine(this.dataset.key, select.value, Number(this.dataset.quantity));
        await api.refresh();
        const announcement = document.getElementById('ShoppingActionStatus');
        if (announcement) announcement.textContent = 'Your item options have been updated.';
        const target = document.querySelector('cart-drawer.active .drawer__close') || document.querySelector('cart-items h1');
        if (target) { if (!target.matches('button, a, input, select, textarea')) target.setAttribute('tabindex', '-1'); target.focus(); }
      } catch (error) {
        this.needsReview = true;
        status.textContent = `${error.message} Check your cart before trying again; a replacement may already have been added.`;
        this.querySelector('[data-cart-review]').hidden = false;
      } finally { api.busy = false; if (!this.needsReview) this.button.disabled = false; }
    }
  }
  customElements.define('cart-variant-editor', CartVariantEditor);
})();
